import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { lastValueFrom } from 'rxjs';
import {
  projectTaskPath,
  viewTaskSamplingPortalPath,
} from '../../../cross-team-shared/hub-paths';
import { TaskResource } from '../../../shared/resources/task-resource.service';
import { Task, TaskExternal } from '../../../team-apis/otterdam/task';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { isRedirectResponse } from '../../shared/navigation/is-redirect-response';
import { AbstractResource, HeadersOrParams } from '../abstract-resource';
import { TemplateLoadingService } from './template-loading.service';

@Injectable()
export class TaskLoadingService extends AbstractResource {
  private templateLoadingService = inject(TemplateLoadingService);
  private taskResourceService = inject(TaskResource);
  private store = inject(TaskWorkspaceStore);

  async loadForProduction() {
    const { projectId, stepId, qaGroupId, templateId } = this.workspaceConfig;

    if (templateId) {
      return this.templateLoadingService.load(templateId);
    }

    const token = await this.getToken('access_token');

    const response = await this.taskResourceService.reserveTask(
      projectId,
      stepId!,
      qaGroupId!,
      token,
    );

    if (isRedirectResponse(response)) {
      this.browserControlService.navigateTo(response.redirect_to);
      throw new Error('Redirect failed'); // should never be reached
    }

    return convertToInternal(response);
  }

  async loadForSampling(position: number) {
    const { projectId, sampleId } = this.workspaceConfig;

    const token = await this.getToken('access_token');

    const task = await lastValueFrom(
      this.request$<TaskExternal>(
        'GET',
        `${environment.sh3BaseUrl}${viewTaskSamplingPortalPath(projectId, sampleId!)}`,
        {
          params: { position: position.toString() },
          customErrorMessage: 'Failed to load task.',
          headers: { Authorization: `Bearer ${token}` },
        },
      ),
    );

    return convertToInternal(task);
  }

  async loadForReadOnly(
    submissionId = this.store.state().selectedSubmissionId,
  ) {
    const { projectId, initialTaskId, templateId } = this.workspaceConfig;
    if (templateId) {
      return this.templateLoadingService.load(templateId);
    }

    const params: HeadersOrParams = {};
    if (submissionId) {
      params.submission_id = submissionId.toString();
    }

    const token = await this.getToken('access_token');

    const path = `${environment.sh3BaseUrl}${projectTaskPath(projectId, initialTaskId!)}`;
    const task = await lastValueFrom(
      this.request$<TaskExternal>('GET', path, {
        params,
        customErrorMessage: 'Failed to load task.',
        headers: { Authorization: `Bearer ${token}` },
      }),
    );

    return convertToInternal(task);
  }
}

function convertToInternal(task: TaskExternal): Task {
  return { ...task, _extras: task._extras ? task._extras : {} };
}
